import { AnimatePresence, motion } from "motion/react";
import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useState } from "react";
import { twJoin } from "tailwind-merge";

import { AlertCircleIcon } from "@/components/Icons/AlertCircleIcon";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { CheckCircleIcon } from "@/components/Icons/CheckCircleIcon";

type FlashToastType = "success" | "warning" | "error" | "info";

export interface FlashToastProps {
  title: string;
  description?: string;
  type: FlashToastType;
  duration?: number;
  shouldAutoClose?: boolean;
  "data-testid"?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export function FlashToast(props: FlashToastProps): React.ReactNode {
  const getIcon = () => {
    switch (props.type) {
      case "error":
        return <AlertCircleIcon />;
      case "success":
        return <CheckCircleIcon />;
      case "warning":
        return <AlertTriangleIcon />;
      case "info":
      default:
        return <AlertCircleIcon />;
    }
  };

  return (
    <div
      className={twJoin(
        "flex items-center gap-8 rounded-3px rounded-t-none p-4 shadow-sm sm:min-w-96",
        props.type === "error" && "bg-red-100",
        props.type === "info" && "bg-blue-100",
        props.type === "success" && "bg-green-100",
        props.type === "warning" && "bg-yellow-100",
      )}
      data-testid={props["data-testid"]}
    >
      <div
        className={twJoin(
          "flex items-center gap-3",
          props.type === "error" && "[&_*]:text-red-800",
          props.type === "info" && "[&_*]:text-blue-800",
          props.type === "success" && "[&_*]:text-green-800",
          props.type === "warning" && "[&_*]:text-yellow-800",
        )}
      >
        <div className="flex h-6 shrink-0 items-center justify-center">{getIcon()}</div>
        <div className="flex flex-col">
          <span className={twJoin(props.description ? "text-caption-bold" : "text-caption")}>{props.title}</span>
          {props.description && <span className="block whitespace-pre-wrap text-caption">{props.description}</span>}
        </div>
      </div>
      {props.action && (
        <button
          type="button"
          onClick={props.action.onClick}
          className="ml-auto min-h-6 shrink-0 text-caption-bold underline"
        >
          {props.action.label}
        </button>
      )}
    </div>
  );
}

const FlashToastsContext = createContext<{
  show: (props: FlashToastProps) => void;
}>({
  show: () => {},
});

export function useFlashToast(): (props: FlashToastProps) => void {
  const { show } = useContext(FlashToastsContext);

  if (!show) {
    throw new Error("useFlashToast must be used within a FlashToastProvider");
  }

  return show;
}

export function FlashToastProvider({ children }: PropsWithChildren<{}>): React.ReactNode {
  const [current, setCurrent] = useState<FlashToastProps | null>(null);

  const show = useCallback((props: FlashToastProps) => {
    setCurrent(props);

    let timer: ReturnType<typeof setTimeout> | null = null;
    if (props.shouldAutoClose) {
      // Clear any existing timeout to prevent multiple toasts from stacking
      timer = setTimeout(() => {
        setCurrent(null);
      }, props.duration);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  return (
    <FlashToastsContext
      value={{
        show: (props: FlashToastProps) =>
          show({
            ...props,
            duration: props.duration ?? 3000,
            shouldAutoClose: props.shouldAutoClose ?? true,
          }),
      }}
    >
      <div className="fixed left-1/2 top-0 z-[70] mx-auto max-w-full -translate-x-1/2">
        <AnimatePresence>
          {current != null && (
            <motion.div
              initial={{
                y: "-100%",
                scale: 0.3,
              }}
              animate={{
                y: "0%",
                scale: 1,
              }}
              exit={{
                y: "-100%",
                scale: 0.3,
              }}
              key={JSON.stringify(current)}
            >
              <FlashToast {...current} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {children}
    </FlashToastsContext>
  );
}
